import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

// Helper to get saved accounts from localStorage
const getSavedAccountsFromStorage = () => {
  try {
    const saved = localStorage.getItem('savedAccounts');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

// Helper to save accounts to localStorage
const saveAccountsToStorage = (accounts) => {
  localStorage.setItem('savedAccounts', JSON.stringify(accounts));
};

// Helper to get active account ID from localStorage
const getActiveAccountIdFromStorage = () => {
  return localStorage.getItem('activeAccountId') || null;
};

// Helper to save active account ID to localStorage
const saveActiveAccountIdToStorage = (accountId) => {
  if (accountId) {
    localStorage.setItem('activeAccountId', accountId);
  } else {
    localStorage.removeItem('activeAccountId');
  }
};

export function AuthProvider({ children }) {
  const initializedRef = useRef(false);
  
  // Current active user state
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  
  // Loading state
  const [loading, setLoading] = useState(() => {
    return !!localStorage.getItem('token');
  });
  
  // Multi-account states
  const [savedAccounts, setSavedAccounts] = useState(() => getSavedAccountsFromStorage());
  const [activeAccountId, setActiveAccountId] = useState(() => getActiveAccountIdFromStorage());

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const token = localStorage.getItem('token');
    
    if (token) {
      // Verify token is still valid
      authAPI.getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
          
          // Use 'id' field from backend (backend uses 'id' not 'account_id')
          const userId = res.data.id;
          
          // Initialize multi-account system if not already done
          const existingAccounts = getSavedAccountsFromStorage();
          if (existingAccounts.length === 0 && res.data) {
            // Add current account to saved accounts
            const currentAccount = {
              id: userId,
              account_id: userId, // Keep for backwards compatibility
              email: res.data.email,
              balance: res.data.balance,
              demo_balance: res.data.demo_balance,
              account_type: res.data.account_type,
              session_type: res.data.session_type,
              token: token,
            };
            setSavedAccounts([currentAccount]);
            saveAccountsToStorage([currentAccount]);
            setActiveAccountId(userId);
            saveActiveAccountIdToStorage(userId);
          } else if (!activeAccountId && res.data) {
            setActiveAccountId(userId);
            saveActiveAccountIdToStorage(userId);
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    }
  }, [activeAccountId]);

  const login = async (credentials) => {
    const loginData = typeof credentials === 'string' 
      ? { email: credentials, password: arguments[1] }
      : credentials;
    
    const res = await authAPI.login(loginData);
    const { access_token, user: userData } = res.data;
    
    // Use 'id' field from backend
    const userId = userData.id;
    
    // Set as current user
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Add to saved accounts if not exists
    const accountData = {
      id: userId,
      account_id: userId, // Keep for backwards compatibility
      email: userData.email,
      balance: userData.balance,
      demo_balance: userData.demo_balance,
      account_type: userData.account_type,
      session_type: userData.session_type,
      token: access_token,
    };
    
    const existingAccounts = getSavedAccountsFromStorage();
    const existingIndex = existingAccounts.findIndex(a => (a.id || a.account_id) === userId);
    
    let updatedAccounts;
    if (existingIndex >= 0) {
      // Update existing account
      updatedAccounts = [...existingAccounts];
      updatedAccounts[existingIndex] = accountData;
    } else {
      // Add new account
      updatedAccounts = [...existingAccounts, accountData];
    }
    
    setSavedAccounts(updatedAccounts);
    saveAccountsToStorage(updatedAccounts);
    setActiveAccountId(userId);
    saveActiveAccountIdToStorage(userId);
    
    return userData;
  };

  const register = async (data) => {
    const res = await authAPI.register(data);
    const { access_token, user: userData } = res.data;
    
    // Use 'id' field from backend
    const userId = userData.id;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Add to saved accounts
    const accountData = {
      id: userId,
      account_id: userId, // Keep for backwards compatibility
      email: userData.email,
      balance: userData.balance,
      demo_balance: userData.demo_balance,
      account_type: userData.account_type,
      session_type: userData.session_type,
      token: access_token,
    };
    
    const updatedAccounts = [...savedAccounts, accountData];
    setSavedAccounts(updatedAccounts);
    saveAccountsToStorage(updatedAccounts);
    setActiveAccountId(userId);
    saveActiveAccountIdToStorage(userId);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('savedAccounts');
    localStorage.removeItem('activeAccountId');
    setUser(null);
    setSavedAccounts([]);
    setActiveAccountId(null);
  };

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    // Use 'id' field from backend
    const userId = updatedUser.id;
    
    // Also update in saved accounts
    const existingAccounts = getSavedAccountsFromStorage();
    const existingIndex = existingAccounts.findIndex(a => (a.id || a.account_id) === userId);
    if (existingIndex >= 0) {
      existingAccounts[existingIndex] = {
        ...existingAccounts[existingIndex],
        balance: updatedUser.balance,
        demo_balance: updatedUser.demo_balance,
        account_type: updatedUser.account_type,
        session_type: updatedUser.session_type,
      };
      setSavedAccounts(existingAccounts);
      saveAccountsToStorage(existingAccounts);
    }
  }, []);

  // Add a new account without switching to it
  const addAccount = useCallback((token, userData) => {
    // Use 'id' field from backend
    const userId = userData.id;
    
    const accountData = {
      id: userId,
      account_id: userId, // Keep for backwards compatibility
      email: userData.email,
      balance: userData.balance,
      demo_balance: userData.demo_balance,
      account_type: userData.account_type,
      session_type: userData.session_type,
      token: token,
    };
    
    const existingAccounts = getSavedAccountsFromStorage();
    const existingIndex = existingAccounts.findIndex(a => (a.id || a.account_id) === userId);
    
    let updatedAccounts;
    if (existingIndex >= 0) {
      // Update existing account
      updatedAccounts = [...existingAccounts];
      updatedAccounts[existingIndex] = accountData;
    } else {
      // Add new account
      updatedAccounts = [...existingAccounts, accountData];
    }
    
    setSavedAccounts(updatedAccounts);
    saveAccountsToStorage(updatedAccounts);
  }, []);

  // Switch to a different saved account
  const switchAccount = useCallback(async (accountId) => {
    const accounts = getSavedAccountsFromStorage();
    const targetAccount = accounts.find(a => (a.id || a.account_id) === accountId);
    
    if (!targetAccount) {
      throw new Error('Account not found');
    }
    
    // Set the token and verify it's still valid
    localStorage.setItem('token', targetAccount.token);
    
    try {
      const res = await authAPI.getMe();
      const userData = res.data;
      
      // Use 'id' field from backend
      const userId = userData.id;
      
      // Update the user state
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update active account
      setActiveAccountId(userId);
      saveActiveAccountIdToStorage(userId);
      
      // Update the account data in saved accounts
      const updatedAccount = {
        ...targetAccount,
        id: userId,
        account_id: userId, // Keep for backwards compatibility
        balance: userData.balance,
        demo_balance: userData.demo_balance,
        account_type: userData.account_type,
        session_type: userData.session_type,
      };
      
      const updatedAccounts = accounts.map(a => 
        (a.id || a.account_id) === accountId ? updatedAccount : a
      );
      setSavedAccounts(updatedAccounts);
      saveAccountsToStorage(updatedAccounts);
      
      return userData;
    } catch (error) {
      // Token is invalid, remove this account
      const updatedAccounts = accounts.filter(a => (a.id || a.account_id) !== accountId);
      setSavedAccounts(updatedAccounts);
      saveAccountsToStorage(updatedAccounts);
      throw new Error('Session expired. Please login again.');
    }
  }, []);

  // Remove an account from saved accounts
  const removeAccount = useCallback((accountId) => {
    if (accountId === activeAccountId) {
      throw new Error('Cannot remove active account');
    }
    
    const accounts = getSavedAccountsFromStorage();
    const updatedAccounts = accounts.filter(a => (a.id || a.account_id) !== accountId);
    setSavedAccounts(updatedAccounts);
    saveAccountsToStorage(updatedAccounts);
  }, [activeAccountId]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      updateUser,
      // Multi-account features
      savedAccounts,
      activeAccountId,
      addAccount,
      switchAccount,
      removeAccount,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
